import React, { useState, useCallback } from 'react';
import { useAppContext, useAppActions } from '../../context/AppContext';
import { Lock, UserCheck, Shield, AlertTriangle, Eye, EyeOff, KeyRound, Mail } from 'lucide-react';
import AccountLockoutService from '../../services/security/AccountLockoutService';
import { useToast } from '../Common/ToastProvider';
import logo from '../../Logo/medfast-logo.png';

const getPasswordStrength = (pw) => {
  if (!pw) return null;
  const hasMixed = /[a-z]/.test(pw) && /[A-Z]/.test(pw);
  const hasNumber = /\d/.test(pw);
  if (pw.length >= 8 && hasMixed && hasNumber) return { label: 'Strong', color: 'bg-emerald-500', width: '100%' };
  if (pw.length >= 6) return { label: 'Moderate', color: 'bg-amber-500', width: '66%' };
  return { label: 'Weak', color: 'bg-red-500', width: '33%' };
};

const Login = ({ onLogin }) => {
  const { state } = useAppContext();
  const actions = useAppActions();
  const { loginCredentials, loginError } = state;
  const { showToast } = useToast();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);

  // Per-field validation
  const [touched, setTouched] = useState({ username: false, password: false });

  // Forgot-password inline flow
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  // MFA step
  const [mfaMode, setMfaMode] = useState(false);
  const [mfaCode, setMfaCode] = useState('');

  const lockoutStatus = loginCredentials.username
    ? AccountLockoutService.getStatus(loginCredentials.username)
    : { locked: false, failedAttempts: 0 };

  const isMfaRequired = mfaMode || (loginError && /mfa/i.test(loginError));

  const handleBlur = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || lockoutStatus.locked) return;

    if (isMfaRequired) {
      if (mfaCode.length !== 6 || !/^\d{6}$/.test(mfaCode)) {
        showToast('Enter a valid 6-digit code', 'warning');
        return;
      }
      setIsSubmitting(true);
      try {
        await onLogin();
        setMfaMode(false);
        setMfaCode('');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setTouched({ username: true, password: true });
    if (!loginCredentials.username || !loginCredentials.password) return;

    setIsSubmitting(true);
    try {
      await onLogin();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotSubmit = (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      showToast('Please enter your email or username', 'warning');
      return;
    }
    showToast('If an account exists, a reset link has been sent.', 'success');
    setForgotEmail('');
    setForgotOpen(false);
  };

  const handleSSOClick = (provider) => {
    showToast(`${provider} SSO integration coming soon`, 'info');
  };

  const passwordStrength = getPasswordStrength(loginCredentials.password);
  const usernameError = touched.username && !loginCredentials.username;
  const passwordError = touched.password && !loginCredentials.password;

  // ── MFA verification step ────────────────────────────────────────────
  if (isMfaRequired) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl w-full max-w-md border border-white/20 overflow-hidden">
          <div className="px-6 pt-6 pb-5 flex flex-col gap-4">
            <div className="flex justify-center">
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                <KeyRound className="w-7 h-7 text-white" />
              </div>
            </div>
            <div className="text-center space-y-1">
              <h2 className="text-lg font-bold text-gray-900">Two-Factor Authentication</h2>
              <p className="text-xs text-gray-500">Enter the 6-digit code from your authenticator app</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full min-h-[48px] text-center text-2xl font-mono tracking-[0.5em] border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white text-gray-900 placeholder-gray-300"
                placeholder="••••••"
                autoFocus
                autoComplete="one-time-code"
              />
              <button
                type="submit"
                disabled={isSubmitting || mfaCode.length !== 6}
                className="w-full min-h-[44px] bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/30 transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSubmitting ? (
                  <div className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                ) : (
                  <Shield className="w-4 h-4" />
                )}
                {isSubmitting ? 'Verifying...' : 'Verify Code'}
              </button>
            </form>
            <button
              type="button"
              onClick={() => { setMfaMode(false); setMfaCode(''); }}
              className="text-xs text-purple-600 hover:text-purple-800 font-medium text-center transition-colors"
            >
              &larr; Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main login form ──────────────────────────────────────────────────
  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl w-full max-w-md shrink-0 h-fit border border-white/20 overflow-hidden">
        <div className="px-5 pt-5 pb-4 flex flex-col gap-3">
          {/* Logo */}
          <div className="flex justify-center">
            <div className="relative h-16 w-[230px] overflow-hidden sm:h-[77px] sm:w-[276px]">
              <img
                src={logo}
                alt="MedFast Aged Care"
                className="absolute -left-[14px] top-1/2 h-[120px] w-auto max-w-none -translate-y-1/2 object-left object-contain drop-shadow-lg sm:-left-[17px] sm:h-[144px]"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (process.env.NODE_ENV === 'development') {
                    console.error('[Login] Logo failed to load');
                  }
                }}
              />
            </div>
          </div>
          <div className="text-center space-y-0.5">
            <h1 className="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              MedFast Aged Care
            </h1>
            <p className="text-gray-600 text-xs">Healthcare Management Platform</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-2.5">
            {loginError && !isMfaRequired && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-2.5 py-1 rounded-lg animate-shake">
                <p className="text-xs font-medium">{loginError}</p>
              </div>
            )}

            {lockoutStatus.locked && (
              <div className="bg-amber-50 border-l-4 border-amber-500 text-amber-800 px-2.5 py-1.5 rounded-lg flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <p className="text-xs font-medium">
                  Account locked due to too many failed attempts. Try again in {lockoutStatus.remainingMinutes} minute(s).
                </p>
              </div>
            )}

            {lockoutStatus.failedAttempts > 0 && !lockoutStatus.locked && (
              <div className="bg-amber-50 border-l-4 border-amber-400 text-amber-700 px-2.5 py-1 rounded-lg">
                <p className="text-xs">
                  {5 - lockoutStatus.failedAttempts} attempt(s) remaining before lockout
                </p>
              </div>
            )}

            {/* Username field */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-0.5" htmlFor="login-username">
                Username
              </label>
              <div className="relative">
                <UserCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-purple-500" aria-hidden />
                <input
                  id="login-username"
                  type="text"
                  autoComplete="username"
                  value={loginCredentials.username}
                  onChange={(e) => actions.setLoginCredentials({ ...loginCredentials, username: e.target.value })}
                  onBlur={() => handleBlur('username')}
                  className={`w-full min-h-[44px] pl-9 pr-3 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white text-gray-900 placeholder-gray-400 text-sm ${usernameError ? 'border-red-400' : 'border-gray-300'}`}
                  placeholder="Username"
                  disabled={lockoutStatus.locked}
                />
              </div>
              {usernameError && (
                <p className="text-red-500 text-xs mt-0.5 ml-1">Username is required</p>
              )}
            </div>

            {/* Password field */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-0.5" htmlFor="login-password">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-purple-500" aria-hidden />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={loginCredentials.password}
                  onChange={(e) => actions.setLoginCredentials({ ...loginCredentials, password: e.target.value })}
                  onBlur={() => handleBlur('password')}
                  className={`w-full min-h-[44px] pl-9 pr-10 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white text-gray-900 placeholder-gray-400 text-sm ${passwordError ? 'border-red-400' : 'border-gray-300'}`}
                  placeholder="Password"
                  disabled={lockoutStatus.locked}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-purple-600 transition-colors focus:outline-none"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordError && (
                <p className="text-red-500 text-xs mt-0.5 ml-1">Password is required</p>
              )}

              {/* Password strength meter */}
              {loginCredentials.password && passwordStrength && (
                <div className="mt-1.5 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${passwordStrength.color}`}
                      style={{ width: passwordStrength.width }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-16 text-right">{passwordStrength.label}</span>
                </div>
              )}

              {/* Forgot password */}
              <div className="flex justify-end mt-1">
                <button
                  type="button"
                  onClick={() => setForgotOpen((v) => !v)}
                  className="text-xs text-purple-600 hover:text-purple-800 font-medium transition-colors"
                >
                  Forgot Password?
                </button>
              </div>

              {forgotOpen && (
                <div className="mt-1 p-3 bg-purple-50/60 rounded-xl border border-purple-200 space-y-2 animate-[fadeIn_0.2s_ease-out]">
                  <p className="text-xs text-gray-600">Enter your email or username to receive a reset link.</p>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-purple-400" aria-hidden />
                    <input
                      type="text"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="w-full min-h-[40px] pl-9 pr-3 py-1.5 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-transparent text-sm bg-white"
                      placeholder="Email or username"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleForgotSubmit}
                    className="w-full min-h-[36px] bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-lg transition-colors"
                  >
                    Send Reset Link
                  </button>
                </div>
              )}
            </div>

            {/* Remember device checkbox */}
            <label className="flex items-center gap-2 cursor-pointer select-none group">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 transition"
              />
              <span className="text-xs text-gray-600 group-hover:text-gray-800 transition-colors">
                Remember this device
              </span>
            </label>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting || lockoutStatus.locked}
              className="w-full min-h-[44px] bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/30 transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isSubmitting ? (
                <div className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              ) : (
                <Lock className="w-3.5 h-3.5" aria-hidden />
              )}
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          {/* SSO divider + buttons */}
          <div className="flex items-center gap-3 my-1">
            <div className="flex-1 h-px bg-gray-300" />
            <span className="text-xs text-gray-400 whitespace-nowrap">or sign in with</span>
            <div className="flex-1 h-px bg-gray-300" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleSSOClick('Microsoft AD')}
              className="min-h-[40px] flex items-center justify-center gap-1.5 border border-gray-300 rounded-xl text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-purple-300 transition-all"
            >
              <svg viewBox="0 0 21 21" className="w-4 h-4" aria-hidden><rect x="1" y="1" width="9" height="9" fill="#F25022"/><rect x="11" y="1" width="9" height="9" fill="#7FBA00"/><rect x="1" y="11" width="9" height="9" fill="#00A4EF"/><rect x="11" y="11" width="9" height="9" fill="#FFB900"/></svg>
              Microsoft AD
            </button>
            <button
              type="button"
              onClick={() => handleSSOClick('SAML SSO')}
              className="min-h-[40px] flex items-center justify-center gap-1.5 border border-gray-300 rounded-xl text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-purple-300 transition-all"
            >
              <KeyRound className="w-4 h-4 text-gray-500" aria-hidden />
              SAML SSO
            </button>
          </div>

          <div className="flex items-center justify-center gap-1 text-xs text-gray-400 mt-1">
            <Shield className="w-3 h-3" />
            <span>TLS 1.3 encrypted &middot; AES-256 at rest &middot; 30-min idle timeout</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
